import { useEffect, useRef, useState } from 'react';
import { DARK_CANVAS, LIGHT_CANVAS } from '@featit/shared/brand/colour-twins';
import {
  WAITING_MOTION,
  waitingReducedAlpha,
  waitingLaunchRadius,
  waitingSmallRadius,
  type IWaitingCanvas,
  type WaitingSizeType,
  type WaitingStudyId,
  waitingStudy,
} from '@featit/shared/brand/waiting-motion';
import { registerWaitingPainter, waitingGroundChanges } from './waiting/waitingLoop';
import { sessionWaitingStudy } from './waiting/waitingSession';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { common } from '@featit/shared/i18n/messages/en/common';

registerEnglishArea('common', common);

/**
 * F-0578 (`docs/concept-waiting-motion.md`): the one sign that something is loading. It draws this
 * session's waiting study (one of ten, picked at random per launch, WAIT-3/WAIT-4) in place of the
 * FEATit mark and every spinner (WAIT-1, WAIT-7). Jay, 2026-10-16: "everywhere we have waiting icons
 * we should have that randomized spinners".
 *
 * Sizes (WAIT-6): `launch` centres a unit of 13% of the window's short side, at most 80 px;
 * `section` is 40 px, for a page, panel or list that is loading; `inline` is 16 px, inside a busy
 * button or a row.
 *
 * Announcing (WAIT-11): a launch or section wait is `role="status"` with a label; an inline wait is
 * silent, and its button says it is busy. Given a `label`, an inline wait is announced too, for a
 * row whose wait is its only content. The drawing is always `aria-hidden`.
 */
export interface IWaitingProps {
  size: WaitingSizeType;
  /** The status a launch or section wait announces; defaults to *Opening FEATit* / *Loading*. */
  label?: string;
  /**
   * The text beside the wait already announces it (a visible or `sr-only` *Loading…*, or a
   * container that is itself the status): the wait adds nothing for a screen reader.
   */
  silent?: boolean;
  className?: string;
  /**
   * WAIT-8: the step this wait is on (*Loading your work area…*), shown under a launch or section
   * wait once it has lasted 2 s. Only the caller knows its step. An inline wait never adds words.
   */
  caption?: string;
  /**
   * WAIT-8: *Try again* beside the slow line, which every section wait shows at 10 s (T-0670): the
   * caller's own read, issued again.
   */
  onRetry?: () => void;
  /**
   * WAIT-9: `performance.now()` when the content came in. From then the study eases to its rest
   * shape over `WAITING_MOTION.settleMs` and stops painting.
   */
  settleFrom?: number;
  /**
   * WAIT-5: a picture of one study, for the setting's choices: drawn on its own clock, never a
   * status, and not the session's study (a preview is not a wait).
   */
  preview?: WaitingStudyId;
  /**
   * WAIT-13: the element whose ground the study is drawn on, when the wait is placed over it rather
   * than inside it (a gated control's pending study, drawn in a portal over the control's icon).
   */
  groundOf?: Element | null;
}

/** WAIT-8: the caption, then the slow line, on the named delays; nothing before 2 s. */
function useCaptionStage(enabled: boolean): 0 | 1 | 2 {
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const [first, , slow] = WAITING_MOTION.captionsMs;
    const timers = [setTimeout(() => setStage(1), first), setTimeout(() => setStage(2), slow)];
    return () => timers.forEach(clearTimeout);
  }, [enabled]);
  return stage;
}

const WAITING_WORDS = 'text-[13px] leading-5 text-gray-500 dark:text-gray-400';

/**
 * WAIT-8 (T-0670): what a wait that has gone on 10 s says — *Still loading. The network may be
 * slow.* — with *Try again* where it can retry. Every section wait shows it; a wait drawn inline
 * beside its own words (the roadmap's *Loading release plan*) puts it under them, so the words and
 * the delay are this one component's.
 */
export function WaitingSlowLine({
  onRetry,
  className,
  compact = false,
  quietWords,
}: {
  onRetry?: () => void;
  className?: string;
  /** One line of room: the short words, and the link beside them, never wrapped. */
  compact?: boolean;
  /**
   * The wait is already drawn elsewhere on the page, with its own Try again: this place says only
   * these words, at the same 10 s, with no second line and no second button.
   */
  quietWords?: string;
}) {
  const t = useT();
  const stage = useCaptionStage(true);
  // The live region is there from the start, empty and taking no room, so the line is announced
  // when it arrives.
  return (
    <span
      aria-live="polite"
      data-waiting-slow={stage >= 2 ? '' : undefined}
      className={
        stage >= 2
          ? `flex items-center gap-2 ${compact || quietWords ? 'whitespace-nowrap' : ''} ${WAITING_WORDS} ${className ?? ''}`
              .replace(/\s+/g, ' ')
              .trim()
          : ''
      }
    >
      {stage >= 2 && quietWords && quietWords}
      {stage >= 2 && !quietWords && (
        <>
          {compact ? t('common.waiting.slowLineShort') : t('common.waiting.slowLine')}
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded font-medium evx-link focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              {t('common.waiting.tryAgain')}
            </button>
          )}
        </>
      )}
    </span>
  );
}

const DEFAULT_LABEL: Record<WaitingSizeType, MessageKey | undefined> = {
  launch: 'common.waiting.opening',
  section: 'common.waiting.loading',
  inline: undefined,
};

function shortSide(): number {
  return typeof window === 'undefined' ? 0 : Math.min(window.innerWidth, window.innerHeight);
}

function useLaunchBox(enabled: boolean): number {
  const [side, setSide] = useState(shortSide);
  useEffect(() => {
    if (!enabled) return undefined;
    const onResize = () => setSide(shortSide());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled]);
  return side;
}

/** WAIT-10: read on every frame, so turning the preference on stops the motion at once. */
function reducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

function darkTheme(): boolean {
  return document.documentElement.classList.contains('dark');
}

/** `rgb(…)` / `rgba(…)` as the browser computes it → channels and alpha; anything else is clear. */
function parseComputed(colour: string): { rgb: number[]; a: number } {
  const parts = /^rgba?\(([^)]+)\)$/
    .exec(colour.trim())?.[1]
    .split(/[\s,/]+/)
    .filter(Boolean);
  if (!parts || parts.length < 3) return { rgb: [0, 0, 0], a: 0 };
  return { rgb: parts.slice(0, 3).map(Number), a: parts[3] === undefined ? 1 : Number(parts[3]) };
}

/**
 * WAIT-13 (I-1689): the colour the study is drawn on, as the screen shows it — every background
 * from the page down to the wait's own parent laid one over the other, so a translucent fill (a
 * dark secondary button's white at 10%) counts with what is under it. A busy button's study sits
 * on the button's fill; a section's on its panel or card. With no fill anywhere, the page canvas.
 */
export function waitingGround(element: Element | null, dark: boolean): string {
  const layers: string[] = [];
  for (let node = element; node; node = node.parentElement) {
    layers.push(getComputedStyle(node).backgroundColor);
  }
  const canvas = dark ? DARK_CANVAS : LIGHT_CANVAS;
  let rgb = [1, 3, 5].map((i) => parseInt(canvas.slice(i, i + 2), 16));
  for (const layer of layers.reverse()) {
    const { rgb: fill, a } = parseComputed(layer);
    if (a <= 0) continue;
    rgb = rgb.map((under, i) => under + (fill[i] - under) * Math.min(1, a));
  }
  return `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

export function Waiting({
  size,
  label,
  silent = false,
  className,
  caption,
  onRetry,
  settleFrom,
  preview,
  groundOf,
}: IWaitingProps) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [{ study, startedAt }] = useState(() =>
    preview ? { study: waitingStudy(preview), startedAt: 0 } : sessionWaitingStudy(),
  );
  const captioned = Boolean(caption) && size !== 'inline';
  const captionStage = useCaptionStage(captioned && settleFrom === undefined);
  // WAIT-8 (T-0670): every section wait reaches the slow line, captioned or not; a preview is not a
  // wait, and a settling one is over.
  const slowLined = size === 'section' && !preview && settleFrom === undefined;
  const side = useLaunchBox(size === 'launch');
  const launchR = waitingLaunchRadius(side);
  const box =
    size === 'launch'
      ? Math.round(launchR * WAITING_MOTION.launchBoxUnits)
      : size === 'section'
        ? WAITING_MOTION.sectionPx
        : WAITING_MOTION.inlinePx;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || box <= 0) return undefined;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(box * dpr);
    canvas.height = Math.round(box * dpr);
    const minW = WAITING_MOTION.minStroke[size];
    const R = size === 'launch' ? launchR : waitingSmallRadius(study, box, minW);
    const { settleMs } = WAITING_MOTION;
    // WAIT-13, WAIT-12: the ground is resolved at mount and again only when the root's theme,
    // Accents or class changes (`waitingGroundChanges`), never per frame.
    let groundFor = -1;
    let ground = '';
    let dark = false;
    const paint = (now: number) => {
      const seconds = (now - startedAt) / 1000;
      const still = reducedMotion();
      // WAIT-9: from the content's arrival, the rest shape in `settleMs`. The end is a time, not a
      // difference: `(settleFrom + settleMs) - settleFrom` can round to just under `settleMs`, which
      // drew the rest shape (k is 1 to the last bit) and stayed in the loop (I-1751).
      const settled =
        settleFrom === undefined
          ? 0
          : now >= settleFrom + settleMs
            ? 1
            : Math.max(0, (now - settleFrom) / settleMs);
      const k = still ? 1 : 1 - Math.pow(1 - settled, 3);
      const changes = waitingGroundChanges();
      if (groundFor !== changes) {
        groundFor = changes;
        dark = darkTheme();
        ground = waitingGround(groundOf ?? canvas.parentElement, dark);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, box, box);
      ctx.save();
      study.draw(ctx as unknown as IWaitingCanvas, {
        cx: box / 2,
        cy: box / 2,
        R,
        t: still ? 0 : seconds,
        k,
        alpha: still ? waitingReducedAlpha(seconds) : 1,
        dark,
        ground,
        minW,
        compact: size !== 'launch',
      });
      ctx.restore();
      // WAIT-12: a settled wait is a still picture; it leaves the loop.
      if (settled >= 1) queueMicrotask(() => show(false));
    };
    let unregister: (() => void) | null = null;
    const show = (visible: boolean) => {
      if (visible && !unregister) unregister = registerWaitingPainter(paint);
      if (!visible && unregister) {
        unregister();
        unregister = null;
      }
    };
    // WAIT-12: a wait that scrolls out of view stops painting.
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver((entries) => show(entries.some((e) => e.isIntersecting)));
    if (observer) observer.observe(canvas);
    else show(true);
    return () => {
      observer?.disconnect();
      show(false);
    };
  }, [box, launchR, size, startedAt, study, settleFrom, groundOf]);

  const worded = captioned || slowLined;
  const defaultLabel = DEFAULT_LABEL[size];
  const announced =
    silent || preview ? undefined : (label ?? (defaultLabel ? t(defaultLabel) : undefined));
  const drawing = (
    <span
      data-waiting={size}
      data-waiting-study={study.id}
      data-launch-mark={size === 'launch' ? '' : undefined}
      role={announced ? 'status' : undefined}
      aria-label={announced}
      aria-hidden={announced ? undefined : true}
      className={`inline-block shrink-0 align-middle ${worded ? '' : (className ?? '')}`.trim()}
      style={{ width: box, height: box }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="block"
        style={{ width: box, height: box }}
      />
    </span>
  );
  if (!worded) return drawing;
  // One stable box from the first frame, so the words arriving never remount the drawing. Space is
  // kept for a caption only when there is one; the slow line, 10 s in, adds its own.
  return (
    <span
      className={`inline-flex flex-col items-center text-center align-middle ${className ?? ''}`.trim()}
    >
      {drawing}
      {captioned && (
        <span aria-live="polite" className={`mt-2 min-h-5 ${WAITING_WORDS}`}>
          {captionStage >= 1 ? caption : null}
        </span>
      )}
      {slowLined && <WaitingSlowLine onRetry={onRetry} className="mt-1" />}
    </span>
  );
}
